import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { delay, tap } from 'rxjs/operators';

export interface PathwayPattyMessage {
  id: string;
  senderId: 'patty' | 'user';
  text: string;
  timestamp: Date;
  isQuestion?: boolean;
  suggestedCategory?: string;
}

export interface PathwayPattyConversation {
  id: string;
  messages: PathwayPattyMessage[];
}

@Injectable({
  providedIn: 'root',
})
export class MockPathwayPattyService {
  private conversation: PathwayPattyConversation = {
    id: 'patty-conversation',
    messages: [
      {
        id: '1',
        senderId: 'patty',
        text: 'Hi there! I\'m Pathway Patty, your personal career coach. I\'m here to help guide you on your career journey. Let\'s start by getting to know you better!',
        timestamp: new Date('2025-01-01T10:00:00'),
      },
      {
        id: '2',
        senderId: 'patty',
        text: 'What area of work interests you most right now?',
        timestamp: new Date('2025-01-01T10:01:00'),
        isQuestion: true,
      },
      {
        id: '3',
        senderId: 'user',
        text: 'I\'m interested in technology and software development.',
        timestamp: new Date('2025-01-01T10:02:00'),
      },
      {
        id: '4',
        senderId: 'patty',
        text: 'That\'s great! Technology is such an exciting field. What draws you to it - is it the problem-solving aspect, the creativity, or the constant innovation?',
        timestamp: new Date('2025-01-01T10:03:00'),
        isQuestion: true,
      },
      {
        id: '5',
        senderId: 'user',
        text: 'I love the problem-solving and how technology can make a real impact on people\'s lives.',
        timestamp: new Date('2025-01-01T10:04:00'),
      },
      {
        id: '6',
        senderId: 'patty',
        text: 'Wonderful! Based on your interests in technology and your passion for problem-solving that makes an impact, I think you\'d really enjoy exploring the **IT** category.',
        timestamp: new Date('2025-01-01T10:05:00'),
      },
      {
        id: '7',
        senderId: 'patty',
        text: 'The IT category is full of exciting opportunities in software development, cloud computing, and emerging technologies. You\'ll discover cutting-edge innovations and career paths that align perfectly with your interests in problem-solving and making an impact.',
        timestamp: new Date('2025-01-01T10:06:00'),
        suggestedCategory: 'IT',
      },
    ],
  };

  getConversation(): Observable<PathwayPattyConversation> {
    return of(this.conversation).pipe(delay(200));
  }

  sendMessage(messageText: string): Observable<PathwayPattyMessage> {
    // Add user message to conversation for persistence
    const userMessage: PathwayPattyMessage = {
      id: Date.now().toString(),
      senderId: 'user',
      text: messageText,
      timestamp: new Date(),
    };
    this.conversation.messages.push(userMessage);

    // Generate Patty's response based on the conversation
    const pattyResponse = this.generatePattyResponse(messageText);
    
    // Add Patty's response to conversation and return it after a delay
    this.conversation.messages.push(pattyResponse);
    return of(pattyResponse).pipe(delay(1000));
  }

  private generatePattyResponse(userMessage: string): PathwayPattyMessage {
    const lowerMessage = userMessage.toLowerCase();
    
    // Check if user has answered questions
    const questionCount = this.conversation.messages.filter(
      (m) => m.senderId === 'patty' && m.isQuestion
    ).length;
    const userAnswerCount = this.conversation.messages.filter(
      (m) => m.senderId === 'user'
    ).length;

    // If user has answered 2 questions, recommend a category
    if (userAnswerCount >= 2 && !this.conversation.messages.some(m => m.suggestedCategory)) {
      const recommendedCategory = this.getRecommendedCategory(lowerMessage);
      return {
        id: Date.now().toString(),
        senderId: 'patty',
        text: `Based on our conversation, I think you'd really enjoy exploring the **${recommendedCategory}** category. ${this.getCategoryDescription(recommendedCategory)}`,
        timestamp: new Date(),
        suggestedCategory: recommendedCategory,
      };
    }

    // Otherwise, ask follow-up questions or provide encouragement
    if (questionCount === 0) {
      return {
        id: Date.now().toString(),
        senderId: 'patty',
        text: 'What area of work interests you most right now?',
        timestamp: new Date(),
        isQuestion: true,
      };
    } else if (questionCount === 1) {
      return {
        id: Date.now().toString(),
        senderId: 'patty',
        text: 'That\'s great! What draws you to it - is it the problem-solving aspect, the creativity, or the constant innovation?',
        timestamp: new Date(),
        isQuestion: true,
      };
    } else {
      return {
        id: Date.now().toString(),
        senderId: 'patty',
        text: 'Thank you for sharing! I\'m learning more about your interests. Let me think about the best category for you...',
        timestamp: new Date(),
      };
    }
  }

  private getRecommendedCategory(userMessage: string): string {
    if (userMessage.includes('tech') || userMessage.includes('software') || userMessage.includes('programming') || userMessage.includes('code')) {
      return 'IT';
    } else if (userMessage.includes('build') || userMessage.includes('construction') || userMessage.includes('architecture')) {
      return 'Construction';
    } else if (userMessage.includes('health') || userMessage.includes('medical') || userMessage.includes('care')) {
      return 'Medical';
    } else if (userMessage.includes('manufactur') || userMessage.includes('production') || userMessage.includes('factory')) {
      return 'Manufacturing';
    } else if (userMessage.includes('finance') || userMessage.includes('money') || userMessage.includes('invest')) {
      return 'Finance';
    } else if (userMessage.includes('teach') || userMessage.includes('learn') || userMessage.includes('education')) {
      return 'Education';
    } else if (userMessage.includes('farm') || userMessage.includes('agriculture') || userMessage.includes('food')) {
      return 'Agriculture';
    } else if (userMessage.includes('retail') || userMessage.includes('shop') || userMessage.includes('sell')) {
      return 'Retail';
    }
    return 'IT'; // Default
  }

  private getCategoryDescription(category: string): string {
    const descriptions: Record<string, string> = {
      IT: 'This field is full of exciting opportunities in software development, cloud computing, and emerging technologies. Discover cutting-edge innovations and career paths in tech.',
      Construction:
        'Explore the world of building and infrastructure. From sustainable construction practices to innovative project management, this category offers insights into shaping our physical world.',
      Medical:
        'Dive into healthcare innovation and medical advancements. Learn about telemedicine, precision medicine, and the latest developments improving patient care.',
      Manufacturing:
        'Discover how modern manufacturing is evolving with automation, AI, and sustainable practices. Explore careers in industrial innovation and production excellence.',
      Finance:
        'Navigate the financial landscape with insights into investment strategies, fintech innovations, and wealth management. Learn about building financial security and growth.',
      Education:
        'Explore educational technology and learning innovations. From online learning platforms to vocational training, discover how education is transforming.',
      Agriculture:
        'Learn about sustainable farming, agricultural technology, and food systems. Discover how modern agriculture is feeding the world while protecting our planet.',
      Retail:
        'Explore the evolving retail landscape with omnichannel strategies, e-commerce innovations, and customer experience enhancements.',
    };

    return descriptions[category] || 'This category offers diverse opportunities and insights. Explore to discover what interests you most!';
  }
}

